/**
 * @file image-ops.ts
 * @description Unified entry point for all pixel-level image operations.
 *
 * Routing logic:
 *   - If running on Tauri AND canvas >= RUST_PIXEL_THRESHOLD → Rust (rayon-parallel)
 *   - Otherwise → pure-JS fallback (ImageOpsWeb)
 *
 * Coordinators MUST use this module. Never call ImageOpsWeb directly.
 */

import { PlatformService } from './platform-service';
import { ImageOpsWeb, type FilterId, type ColorAdjustParams } from './image-ops-web';

export type { FilterId, ColorAdjustParams };

/** Pixels below this count go through JS (IPC overhead not worth it ≈ 700×700). */
const RUST_PIXEL_THRESHOLD = 500_000;

// ─── Internal invoke helper ───────────────────────────────────────────────────

async function invoke<T>(cmd: string, args: Record<string, unknown>): Promise<T> {
    return (window as any).__TAURI__.core.invoke(cmd, args);
}

function shouldUseRust(pixelCount: number): boolean {
    return PlatformService.isTauri() && pixelCount >= RUST_PIXEL_THRESHOLD;
}

// ─── Public API ───────────────────────────────────────────────────────────────

export class ImageOps {
    /**
     * Applies a named filter to pixel data.
     * @param data  RGBA Uint8ClampedArray
     * @param intensity  0..1, currently used by JS path only
     */
    static async applyFilter(
        data: Uint8ClampedArray, w: number, h: number,
        filterId: FilterId, intensity = 1.0
    ): Promise<Uint8ClampedArray> {
        if (shouldUseRust(w * h)) {
            const bytes: number[] = await invoke('cmd_apply_filter', {
                pixels: Array.from(data), width: w, height: h,
                filter: filterId, intensity,
            });
            return new Uint8ClampedArray(bytes);
        }
        return ImageOpsWeb.applyFilter(data, w, h, filterId, intensity);
    }

    /**
     * Gaussian blur via separable convolution.
     * @param radius  Kernel half-width in pixels (e.g. 3.0 → 7-tap kernel)
     */
    static async gaussianBlur(
        data: Uint8ClampedArray, w: number, h: number,
        radius: number
    ): Promise<Uint8ClampedArray> {
        if (shouldUseRust(w * h)) {
            const bytes: number[] = await invoke('cmd_gaussian_blur', {
                pixels: Array.from(data), width: w, height: h, radius,
            });
            return new Uint8ClampedArray(bytes);
        }
        return ImageOpsWeb.gaussianBlur(data, w, h, radius);
    }

    /**
     * Applies brightness/contrast/saturation/hue/exposure in a single pass.
     * Rust path always used on Tauri regardless of pixel count (one-pass HSL is cheap for IPC).
     */
    static async colorAdjust(
        data: Uint8ClampedArray, w: number, h: number,
        params: ColorAdjustParams
    ): Promise<Uint8ClampedArray> {
        if (PlatformService.isTauri()) {
            const bytes: number[] = await invoke('cmd_color_adjust', {
                pixels: Array.from(data), width: w, height: h,
                brightness: params.brightness ?? 0,
                contrast:   params.contrast   ?? 0,
                saturation: params.saturation ?? 0,
                hue:        params.hue        ?? 0,
                exposure:   params.exposure   ?? 0,
            });
            return new Uint8ClampedArray(bytes);
        }
        return ImageOpsWeb.colorAdjust(data, w, h, params);
    }

    /**
     * BFS flood fill.
     * @param fillColor  [r, g, b, a] 0..255
     * @param tolerance  0..255 per-channel delta
     */
    static async floodFill(
        data: Uint8ClampedArray, w: number, h: number,
        x: number, y: number,
        fillColor: [number, number, number, number],
        tolerance: number
    ): Promise<Uint8ClampedArray> {
        if (shouldUseRust(w * h)) {
            const bytes: number[] = await invoke('cmd_flood_fill', {
                pixels: Array.from(data), width: w, height: h,
                x: Math.floor(x), y: Math.floor(y),
                fillR: fillColor[0], fillG: fillColor[1],
                fillB: fillColor[2], fillA: fillColor[3],
                tolerance,
            });
            return new Uint8ClampedArray(bytes);
        }
        return ImageOpsWeb.floodFill(data, w, h, x, y, fillColor, tolerance);
    }

    /**
     * Encodes pixel data to PNG / JPEG / WebP.
     * Rust path returns raw file bytes (better quality control for JPEG/WebP).
     * Web path falls back to canvas.toBlob().
     */
    static async encodeImage(
        data: Uint8ClampedArray, w: number, h: number,
        format: 'png' | 'jpeg' | 'webp',
        quality = 92
    ): Promise<ArrayBuffer> {
        if (PlatformService.isTauri()) {
            const bytes: number[] = await invoke('cmd_encode_image', {
                pixels: Array.from(data), width: w, height: h,
                format, quality,
            });
            return new Uint8Array(bytes).buffer;
        }
        return ImageOpsWeb.encodeImage(data, w, h, format, quality);
    }
}
